import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, EmptyState, IconButton, Page, PageHeader, Pagination, SearchInput, TableSkeleton } from "../../../components/ui";
import { getPayments, getPaymentsStats } from "../../../services/paymentsLedger";
import { can } from "../../../utils/roles";
import PaymentDetail from "./PaymentDetail";
import PaymentsChart from "./PaymentsChart";
import ThanksComposer from "./ThanksComposer";
import { formatMoney, formatWhen, MethodLabel, StatusBadge, TypeLabel } from "./shared";
import "./payments.css";

const TYPE_FILTERS = [
  { value: "", label: "All" },
  { value: "donation", label: "Donations" },
  { value: "subscriptions", label: "Subscriptions" },
  { value: "exclusive", label: "Exclusive content" },
];

const EMPTY_FILTERS = { type: "", status: "SUCCESS", method: "", account: "", thanked: "", from: "", to: "" };

/**
 * Every payment of the platform (donations, subscriptions, renewals, exclusive
 * content; card, Apple Pay, Google Pay, Mobile Money; with or without an
 * account), with the means to thank the payers and to offer them a gift.
 */
const Payments = () => {
  const { t } = useTranslation();
  const canAct = can("transactions", "edit");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState([]);
  const [opened, setOpened] = useState(null);
  const [composer, setComposer] = useState(null); // { ids, alreadyThanked }

  const key = JSON.stringify({ ...filters, q: search, page });
  const load = useCallback(() => {
    const params = JSON.parse(key);
    getPayments(params).then(setData);
    getPaymentsStats({ ...params, page: undefined }).then(setStats);
  }, [key]);
  useEffect(() => {
    const timer = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  const setFilter = (name) => (value) => {
    setFilters((current) => ({ ...current, [name]: value }));
    setPage(1);
    setSelected([]);
  };
  const items = data?.items || [];
  const selectable = items.filter((item) => item.status === "SUCCESS" && item.payer.email);
  const allSelected = selectable.length > 0 && selectable.every((item) => selected.includes(item.id));
  const toggle = (id) => setSelected((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));
  const toggleAll = () => setSelected(allSelected ? [] : selectable.map((item) => item.id));

  const openComposer = (ids) => {
    const thanked = items.filter((item) => ids.includes(item.id) && item.thanks.length).length;
    setComposer({ ids, alreadyThanked: thanked });
  };

  return (
    <Page>
      <PageHeader
        title={t("Payments")}
        subtitle={t("Every donation, subscription and purchase, with or without an account. Thank the payers or offer them a gift.")}
        actions={<IconButton icon="ri-refresh-line" label={t("Refresh")} onClick={load} />}
      />

      <div className="ledger-stats">
        {(stats?.totals?.length ? stats.totals : [{ currency: "USD", total: null }]).slice(0, 3).map((row) => (
          <div key={row.currency} className="ui-card ledger-stat">
            <strong>{row.total === null ? "—" : formatMoney(row.total, row.currency)}</strong>
            <span>{t("paymentsCount", { count: row.count || 0 })}</span>
          </div>
        ))}
        <div className="ui-card ledger-stat">
          <strong>{stats ? stats.payers : "—"}</strong>
          <span>{t("Different payers")}</span>
        </div>
        <button type="button" className="ui-card ledger-stat" data-tone="amber" onClick={() => setFilter("thanked")("no")}>
          <strong>{stats ? stats.notThanked : "—"}</strong>
          <span>{t("Not thanked yet")}</span>
        </button>
      </div>

      <PaymentsChart filters={{ ...filters, q: search }} />

      <section className="ui-card">
        <div className="ui-toolbar ledger-toolbar">
          <div className="ui-chips">
            {TYPE_FILTERS.map((item) => (
              <button key={item.value} type="button" className="ui-chip" aria-pressed={filters.type === item.value} onClick={() => setFilter("type")(item.value)}>
                {t(item.label)}
              </button>
            ))}
          </div>
          <SearchInput
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            placeholder={t("Name, email, phone or transaction")}
          />
        </div>
        <div className="ledger-filters">
          <select className="ui-input" aria-label={t("status")} value={filters.status} onChange={(e) => setFilter("status")(e.target.value)}>
            <option value="">{t("All statuses")}</option>
            <option value="SUCCESS">{t("Succeeded")}</option>
            <option value="PENDING">{t("Pending")}</option>
            <option value="FAILED">{t("Failed")}</option>
          </select>
          <select className="ui-input" aria-label={t("Payment method")} value={filters.method} onChange={(e) => setFilter("method")(e.target.value)}>
            <option value="">{t("All payment methods")}</option>
            <option value="card">{t("Card, Apple Pay, Google Pay")}</option>
            <option value="mobile">{t("Mobile Money")}</option>
          </select>
          <select className="ui-input" aria-label={t("Account")} value={filters.account} onChange={(e) => setFilter("account")(e.target.value)}>
            <option value="">{t("With or without an account")}</option>
            <option value="registered">{t("Registered users")}</option>
            <option value="guest">{t("Without an account")}</option>
          </select>
          <select className="ui-input" aria-label={t("Thanks")} value={filters.thanked} onChange={(e) => setFilter("thanked")(e.target.value)}>
            <option value="">{t("Thanked or not")}</option>
            <option value="no">{t("Not thanked yet")}</option>
            <option value="yes">{t("Already thanked")}</option>
          </select>
          <label className="ledger-date">
            <span>{t("From")}</span>
            <input type="date" className="ui-input" value={filters.from} onChange={(e) => setFilter("from")(e.target.value)} />
          </label>
          <label className="ledger-date">
            <span>{t("To")}</span>
            <input
              type="date"
              className="ui-input"
              value={filters.to.slice(0, 10)}
              onChange={(e) => setFilter("to")(e.target.value ? `${e.target.value}T23:59:59` : "")}
            />
          </label>
        </div>

        {canAct && selected.length > 0 && (
          <div className="ledger-selection" role="status">
            <span>{t("selectedCount", { count: selected.length })}</span>
            <button type="button" className="ui-btn" onClick={() => openComposer(selected)}>
              <i className="ri-heart-line" aria-hidden="true"></i>
              {t("Thank the selection")}
            </button>
            <button type="button" className="ui-btn ui-btn-secondary" onClick={() => setSelected([])}>
              {t("Cancel")}
            </button>
          </div>
        )}

        {!data ? (
          <TableSkeleton />
        ) : !items.length ? (
          <EmptyState icon="ri-money-dollar-circle-line">{t("No payment")}</EmptyState>
        ) : (
          <div className="ui-table-wrap">
            <table className="ui-table ledger-table">
              <thead>
                <tr>
                  {canAct && (
                    <th>
                      <input type="checkbox" aria-label={t("Select all")} checked={allSelected} onChange={toggleAll} />
                    </th>
                  )}
                  <th>{t("Date")}</th>
                  <th>{t("Payer")}</th>
                  <th>{t("Type")}</th>
                  <th>{t("Amount")}</th>
                  <th>{t("Payment method")}</th>
                  <th>{t("status")}</th>
                  <th>{t("Thanks")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="ledger-row" onClick={() => setOpened(item.id)}>
                    {canAct && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label={t("Select")}
                          disabled={item.status !== "SUCCESS" || !item.payer.email}
                          checked={selected.includes(item.id)}
                          onChange={() => toggle(item.id)}
                        />
                      </td>
                    )}
                    <td className="ui-cell-muted">{formatWhen(item.createdAt)}</td>
                    <td>
                      <span className="ui-media-title">{item.payer.name || item.payer.email || t("Anonymous")}</span>
                      <span className="ui-media-meta">
                        {item.payer.name && item.payer.email ? `${item.payer.email} ` : ""}
                        {!item.payer.registered && <Badge color="slate">{t("No account")}</Badge>}
                      </span>
                    </td>
                    <td>
                      <TypeLabel payment={item} />
                      {item.content && <span className="ui-media-meta ledger-content">{item.content}</span>}
                    </td>
                    <td className="ledger-amount">{formatMoney(item.amount, item.currency)}</td>
                    <td className="ui-cell-muted">
                      <MethodLabel payment={item} />
                    </td>
                    <td>
                      <StatusBadge status={item.status} />
                    </td>
                    <td>
                      {item.thanks.length > 0 && (
                        <Badge color="green">
                          <i className="ri-mail-check-line" aria-hidden="true"></i>
                          {t("Thanked")}
                        </Badge>
                      )}
                      {item.gifts.length > 0 && (
                        <Badge color="violet">
                          <i className="ri-award-line" aria-hidden="true"></i>
                          {t("Gift")}
                        </Badge>
                      )}
                      {item.refundedAmount > 0 && (
                        <Badge color="red">
                          <i className="ri-arrow-go-back-line" aria-hidden="true"></i>
                          {t("Refunded")}
                        </Badge>
                      )}
                      {!item.thanks.length && !item.gifts.length && !(item.refundedAmount > 0) && <span className="ui-cell-muted">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && (
          <Pagination
            page={page}
            pageCount={Math.max(1, Math.ceil(data.total / data.pageSize))}
            total={data.total}
            pageSize={data.pageSize}
            onChange={(value) => {
              setPage(value);
              setSelected([]);
            }}
          />
        )}
      </section>

      {opened && (
        <PaymentDetail
          id={opened}
          canAct={canAct}
          onClose={() => setOpened(null)}
          onThank={(payment) => {
            setOpened(null);
            openComposer([payment.id]);
          }}
          onChange={load}
        />
      )}
      {composer && (
        <ThanksComposer
          ids={composer.ids}
          alreadyThanked={composer.alreadyThanked}
          onClose={() => setComposer(null)}
          onSent={() => {
            setComposer(null);
            setSelected([]);
            setOpened(null);
            load();
          }}
        />
      )}
    </Page>
  );
};

export default Payments;
